
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {TradeHistoryInfoResponse} from './TradeHistoryInfoResponse';
import {RequestDirection} from '../core/RequestDirection';
import {FieldModelRequestDirection} from '../core/RequestDirection';

/**
 * TradeHistoryInfoRequest struct
 */
class TradeHistoryInfoRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!RequestDirection=} requestDirection
   * @param {Date=} requestFrom
   * @param {Date=} requestTo
   * @param {number=} orderId
   * @param {number=} lastRecordId
   * @param {!number=} pageSize
   * @param {!boolean=} latestOrderState
   * @constructor
   */
  constructor (parent = new Request(), requestDirection = new RequestDirection(RequestDirection.FORWARD), requestFrom = null, requestTo = null, orderId = null, lastRecordId = null, pageSize = 1000, latestOrderState = false) {
    super()
    super.copy(parent)
    this.RequestDirection = requestDirection
    this.RequestFrom = requestFrom
    this.RequestTo = requestTo
    this.OrderId = orderId
    this.LastRecordId = lastRecordId
    this.PageSize = pageSize
    this.LatestOrderState = latestOrderState
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradeHistoryInfoRequest}
   * @param {!TradeHistoryInfoRequest} other Other struct
   * @returns {!TradeHistoryInfoRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.RequestDirection != null) {
      this.RequestDirection = RequestDirection.fromObject(other.RequestDirection)
    } else {
      this.RequestDirection = null
    }
    if (other.RequestFrom != null) {
      if (other.RequestFrom instanceof Date) {
        this.RequestFrom = new Date(other.RequestFrom.getTime())
      } else {
        this.RequestFrom = new Date(Math.round(other.RequestFrom / 1000000))
      }
    } else {
      this.RequestFrom = null
    }
    if (other.RequestTo != null) {
      if (other.RequestTo instanceof Date) {
        this.RequestTo = new Date(other.RequestTo.getTime())
      } else {
        this.RequestTo = new Date(Math.round(other.RequestTo / 1000000))
      }
    } else {
      this.RequestTo = null
    }
    if (other.OrderId != null) {
      this.OrderId = UInt64.fromNumber(other.OrderId)
    } else {
      this.OrderId = null
    }
    if (other.LastRecordId != null) {
      this.LastRecordId = UInt64.fromNumber(other.LastRecordId)
    } else {
      this.LastRecordId = null
    }
    if (other.PageSize != null) {
      this.PageSize = other.PageSize
    } else {
      this.PageSize = null
    }
    if (other.LatestOrderState != null) {
      this.LatestOrderState = other.LatestOrderState
    } else {
      this.LatestOrderState = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradeHistoryInfoRequest}
   * @returns {!TradeHistoryInfoRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradeHistoryInfoRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradeHistoryInfoRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradeHistoryInfoRequest}
   * @param {!TradeHistoryInfoRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradeHistoryInfoRequest)) {
      throw new TypeError('Instance of TradeHistoryInfoRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradeHistoryInfoRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      RequestDirection: ((this.RequestDirection != null) ? this.RequestDirection : null),
      RequestFrom: ((this.RequestFrom != null) ? (this.RequestFrom.getTime() * 1000000) : null),
      RequestTo: ((this.RequestTo != null) ? (this.RequestTo.getTime() * 1000000) : null),
      OrderId: ((this.OrderId != null) ? this.OrderId.toNumber() : null),
      LastRecordId: ((this.LastRecordId != null) ? this.LastRecordId.toNumber() : null),
      PageSize: ((this.PageSize != null) ? this.PageSize : null),
      LatestOrderState: ((this.LatestOrderState != null) ? this.LatestOrderState : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradeHistoryInfoRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradeHistoryInfoRequest} other Object value
   * @returns {!TradeHistoryInfoRequest} Created struct
   */
  static fromObject (other) {
    return new TradeHistoryInfoRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradeHistoryInfoRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradeHistoryInfoRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradeHistoryInfoRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5118
  }
}

export { TradeHistoryInfoRequest };

/**
 * Fast Binary Encoding TradeHistoryInfoRequest field model
 */
class FieldModelTradeHistoryInfoRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.RequestDirection = new FieldModelRequestDirection(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.RequestFrom = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, this.RequestDirection.fbeOffset + this.RequestDirection.fbeSize), buffer, this.RequestDirection.fbeOffset + this.RequestDirection.fbeSize)
    this.RequestTo = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, this.RequestFrom.fbeOffset + this.RequestFrom.fbeSize), buffer, this.RequestFrom.fbeOffset + this.RequestFrom.fbeSize)
    this.OrderId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.RequestTo.fbeOffset + this.RequestTo.fbeSize), buffer, this.RequestTo.fbeOffset + this.RequestTo.fbeSize)
    this.LastRecordId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.OrderId.fbeOffset + this.OrderId.fbeSize), buffer, this.OrderId.fbeOffset + this.OrderId.fbeSize)
    this.PageSize = new fbe.FieldModelUInt32(buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize)
    this.LatestOrderState = new fbe.FieldModelBool(buffer, this.PageSize.fbeOffset + this.PageSize.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.RequestDirection.fbeSize + this.RequestFrom.fbeSize + this.RequestTo.fbeSize + this.OrderId.fbeSize + this.LastRecordId.fbeSize + this.PageSize.fbeSize + this.LatestOrderState.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.RequestDirection.fbeExtra + this.RequestFrom.fbeExtra + this.RequestTo.fbeExtra + this.OrderId.fbeExtra + this.LastRecordId.fbeExtra + this.PageSize.fbeExtra + this.LatestOrderState.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradeHistoryInfoRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5118
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.RequestDirection.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RequestDirection.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestDirection.fbeSize

    if ((fbeCurrentSize + this.RequestFrom.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RequestFrom.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestFrom.fbeSize

    if ((fbeCurrentSize + this.RequestTo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RequestTo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestTo.fbeSize

    if ((fbeCurrentSize + this.OrderId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderId.fbeSize

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LastRecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PageSize.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.LatestOrderState.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LatestOrderState.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestOrderState.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!TradeHistoryInfoRequest} fbeValue Default value, defaults is new TradeHistoryInfoRequest()
   * @returns {!TradeHistoryInfoRequest} TradeHistoryInfoRequest value
   */
  get (fbeValue = new TradeHistoryInfoRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!TradeHistoryInfoRequest} fbeValue TradeHistoryInfoRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.RequestDirection.fbeSize) <= fbeStructSize) {
      fbeValue.RequestDirection = this.RequestDirection.get(new RequestDirection(RequestDirection.FORWARD))
    } else {
      fbeValue.RequestDirection = new RequestDirection(RequestDirection.FORWARD)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestDirection.fbeSize

    if ((fbeCurrentSize + this.RequestFrom.fbeSize) <= fbeStructSize) {
      fbeValue.RequestFrom = this.RequestFrom.get()
    } else {
      fbeValue.RequestFrom = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestFrom.fbeSize

    if ((fbeCurrentSize + this.RequestTo.fbeSize) <= fbeStructSize) {
      fbeValue.RequestTo = this.RequestTo.get()
    } else {
      fbeValue.RequestTo = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestTo.fbeSize

    if ((fbeCurrentSize + this.OrderId.fbeSize) <= fbeStructSize) {
      fbeValue.OrderId = this.OrderId.get()
    } else {
      fbeValue.OrderId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderId.fbeSize

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) <= fbeStructSize) {
      fbeValue.LastRecordId = this.LastRecordId.get()
    } else {
      fbeValue.LastRecordId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) <= fbeStructSize) {
      fbeValue.PageSize = this.PageSize.get(1000)
    } else {
      fbeValue.PageSize = 1000
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.LatestOrderState.fbeSize) <= fbeStructSize) {
      fbeValue.LatestOrderState = this.LatestOrderState.get(false)
    } else {
      fbeValue.LatestOrderState = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestOrderState.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {!TradeHistoryInfoRequest} fbeValue TradeHistoryInfoRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradeHistoryInfoRequest}
   * @param {TradeHistoryInfoRequest} fbeValue TradeHistoryInfoRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.RequestDirection.set(fbeValue.RequestDirection)
    this.RequestFrom.set(fbeValue.RequestFrom)
    this.RequestTo.set(fbeValue.RequestTo)
    this.OrderId.set(fbeValue.OrderId)
    this.LastRecordId.set(fbeValue.LastRecordId)
    this.PageSize.set(fbeValue.PageSize)
    this.LatestOrderState.set(fbeValue.LatestOrderState)
  }
}

export { FieldModelTradeHistoryInfoRequest };

/**
 * Fast Binary Encoding TradeHistoryInfoRequest model
 */
class TradeHistoryInfoRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradeHistoryInfoRequest(this.buffer, 4)
  }

  /**
   * Get the TradeHistoryInfoRequest model
   * @this {!TradeHistoryInfoRequestModel}
   * @returns {!FieldModelTradeHistoryInfoRequest} model TradeHistoryInfoRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradeHistoryInfoRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradeHistoryInfoRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradeHistoryInfoRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradeHistoryInfoRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradeHistoryInfoRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradeHistoryInfoRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradeHistoryInfoRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradeHistoryInfoRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradeHistoryInfoRequestModel}
   * @param {!TradeHistoryInfoRequest} value TradeHistoryInfoRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradeHistoryInfoRequestModel}
   * @param {!TradeHistoryInfoRequest} value TradeHistoryInfoRequest value, defaults is new TradeHistoryInfoRequest()
   * @return {!object} Deserialized TradeHistoryInfoRequest value and its size
   */
  deserialize (value = new TradeHistoryInfoRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradeHistoryInfoRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradeHistoryInfoRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradeHistoryInfoRequestModel}
   * @param {!number} prev Previous TradeHistoryInfoRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradeHistoryInfoRequestModel };
TradeHistoryInfoRequest.__has_response = true;
TradeHistoryInfoRequest.__response_class = TradeHistoryInfoResponse;
